import { useState, useCallback } from 'react';
import { Currency, Language, ViewMode } from '../types';

export const useGlobalUI = () => {
    const [currency, setCurrency] = useState<Currency>('AED');
    const [language, setLanguage] = useState<Language>('en');
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [isGateOpen, setIsGateOpen] = useState(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

    // Currency Utils
    const EXCHANGE_RATES: Record<Currency, number> = {
        USD: 1,
        AED: 3.6725,
        EUR: 0.92,
        GBP: 0.79
    };

    const SYMBOLS: Record<Currency, string> = {
        USD: '$',
        AED: 'AED ',
        EUR: '€',
        GBP: '£'
    };

    // CRITICAL: Use useCallback with currency dependency so convertPrice updates when currency changes
    const convertPrice = useCallback((priceStr: string): string => {
        const numericValue = parseInt(priceStr.replace(/[^0-9]/g, ''));
        if (isNaN(numericValue)) return priceStr;

        // Detect Input Currency (Default to USD if no symbol, but Reelly is AED)
        const isInputAED = priceStr.includes('AED');

        // Case 1: Input is AED, Target is AED -> Return as is (preserve backend rounding)
        if (isInputAED && currency === 'AED') {
            return priceStr;
        }

        // Case 2: Input is AED, Target is USD/EUR/GBP
        if (isInputAED) {
             const baseUSD = numericValue / EXCHANGE_RATES['AED']; // Convert AED to USD base
             if (currency === 'USD') {
                 // AED to USD
                 return `${SYMBOLS['USD']}${Math.round(baseUSD).toLocaleString()}`;
             }
             // AED to Other
             const rate = EXCHANGE_RATES[currency];
             const converted = baseUSD * rate;
             return `${SYMBOLS[currency]}${Math.round(converted / 100) * 100}`; 
        }

        // Case 3: Input is USD (Legacy/Default), Target is Other
        if (currency === 'USD') return priceStr;
        const rate = EXCHANGE_RATES[currency];
        const converted = numericValue * rate;
        const rounded = Math.ceil(converted / 100) * 100;
        return `${SYMBOLS[currency]}${rounded.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }, [currency]);

    return {
        currency,
        setCurrency,
        language,
        setLanguage,
        viewMode,
        setViewMode,
        isMobileFiltersOpen,
        setIsMobileFiltersOpen,
        isGateOpen,
        setIsGateOpen,
        isSortMenuOpen,
        setIsSortMenuOpen,
        convertPrice
    };
};
